import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import goldColors from '../themes/goldColors';
import { useLanguage } from '../contexts/LanguageContext';

// 10 ago 2026 (Augusto: "toca mejorar el onboarding, no aparece la
// pantalla de bienvenida que le indica lo que va a pasar y lo guía de la
// mano"): antes del registro llevaba a /profile directo (ver
// RegisterFormSinglePage.js) -- el cliente nuevo aterrizaba en un
// formulario sin ningún contexto de qué iba a pasar ni cuánto le iba a
// tomar. Existía WelcomePage.js ("Inicio" en el sidebar) con un flujo
// parecido, pero es una pantalla vieja con lógica de localStorage/estado
// propio ya parcheada varias veces (ver comentarios ahí mismo) -- se
// deja tal cual para el uso diario de clientes existentes ("Inicio"), y
// se crea esta pantalla nueva, chica y sin dependencias raras, dedicada
// SOLO al primer momento después de registrarse: explica los 3 pasos
// (Perfil, Cuentas, Deudas), cuánto toma, y un solo botón para arrancar.
// No exige sesión activa más allá de estar autenticado (mismo criterio
// que el resto de rutas privadas) y no lee/escribe ningún estado de
// onboarding -- es puramente informativa, el progreso real lo sigue
// llevando OnboardingSidebar.js con /onboarding/data.
//
// 17 sep 2026 (pedido de Augusto: "se puede tener una version completa en
// ingles?"): `steps` se vuelve una función de isSpanish -- es la primera
// pantalla que ve un cliente nuevo tras registrarse, así que vale la pena
// que quede bien traducida.
const makeSteps = (isSpanish) => [
  {
    icon: PersonIcon,
    title: isSpanish ? 'Perfil' : 'Profile',
    desc: isSpanish
      ? 'Un par de datos rápidos (ocupación, estado civil) para adaptar la app a tu situación.'
      : 'A couple of quick details (occupation, marital status) to tailor the app to your situation.',
  },
  {
    icon: AccountBalanceWalletIcon,
    title: isSpanish ? 'Cuentas bancarias' : 'Bank accounts',
    desc: isSpanish
      ? 'Registra tus cuentas y tarjetas para empezar a ver tu dinero real, no solo estimaciones.'
      : 'Register your accounts and cards to start seeing your real money, not just estimates.',
  },
  {
    icon: SavingsIcon,
    title: isSpanish ? 'Deudas' : 'Debts',
    desc: isSpanish
      ? 'Si tienes préstamos o tarjetas con saldo, regístralos -- si no tienes ninguna, avisas y sigues.'
      : "If you have loans or cards with a balance, register them -- if you don't have any, just say so and move on.",
  },
];

const OnboardingIntro = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const steps = makeSteps(isSpanish);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: (theme) => theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            {isSpanish ? '¡Bienvenido/a a PCM!' : 'Welcome to PCM!'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {isSpanish
              ? 'Vamos a configurar tu cuenta en 3 pasos cortos para que puedas ver el control real de tus finanzas desde el primer día.'
              : "Let's set up your account in 3 short steps so you can see real control over your finances from day one."}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {isSpanish
              ? 'Te tomará unos 3-5 minutos. Puedes ir a tu ritmo -- si te falta algo, puedes completarlo después.'
              : "It'll take about 3-5 minutes. You can go at your own pace -- if you're missing something, you can complete it later."}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ textAlign: 'left', mb: 4 }}>
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <Box key={step.title} sx={{ display: 'flex', alignItems: 'flex-start', mb: idx < steps.length - 1 ? 3 : 0 }}>
                  <Avatar sx={{ bgcolor: goldColors.main, mr: 2, width: 40, height: 40 }}>
                    <Icon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {idx + 1}. {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/profile')}
            sx={{
              bgcolor: goldColors.main,
              '&:hover': { bgcolor: goldColors.dark },
              px: 4,
            }}
          >
            {isSpanish ? 'Comenzar' : 'Get Started'}
          </Button>

          {/* 12 ago 2026 (pedido de Augusto, junto con mover Guías/Blog/
              Soporte al menú de la app): en vez de un pop-up aparte que
              interrumpe, el aviso de que existen recursos de ayuda vive
              acá, como parte natural de la bienvenida -- se ve una sola
              vez, en el momento en que más sentido tiene (justo antes de
              que el cliente empiece a usar la app por primera vez). */}
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <MenuBookIcon fontSize="small" sx={{ color: goldColors.main }} />
            <Typography variant="caption">
              {isSpanish
                ? 'Tip: en cualquier momento encuentras una guía completa paso a paso, y soporte, en "Recursos" dentro del menú.'
                : 'Tip: you can find a full step-by-step guide, and support, anytime under "Resources" in the menu.'}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default OnboardingIntro;
